import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Check, Clock, Star } from "lucide-react";
import {
  TASK_CATEGORIES,
  TASK_STATUSES,
  PRIORITY_PILL,
  fmtDate,
} from "./constants";
import { ModalShell, Field } from "../_dialog";

export function TaskRow({ task, compact, onEdit, onComplete, onDelete }) {
  return (
    <div
      className={`flex items-start gap-2 ${compact ? "py-1.5" : "py-2"} text-xs border-b border-slate-100 last:border-0`}
      data-testid={`task-row-${task.id}`}
    >
      <button
        type="button"
        onClick={() => onComplete?.(task)}
        title={task.status === "done" ? "Mark as to do" : "Mark complete"}
        className={`mt-0.5 inline-flex w-4 h-4 rounded-full items-center justify-center border ${
          task.status === "done"
            ? "bg-emerald-600 border-emerald-600 text-white"
            : "bg-white border-slate-300 hover:border-slate-900"
        }`}
        data-testid={`task-complete-${task.id}`}
      >
        {task.status === "done" ? <Check className="w-2.5 h-2.5" /> : null}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className={`font-medium truncate ${
            task.status === "done" ? "text-slate-400 line-through" : "text-slate-900"
          }`}
        >
          {task.is_top_priority && (
            <Star className="w-3 h-3 inline mr-1 text-amber-500 fill-amber-500" />
          )}
          {task.title}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          <span
            className={`inline-flex items-center text-[9px] uppercase tracking-wider font-semibold rounded-full px-1.5 py-0.5 border ${
              PRIORITY_PILL[task.priority || "medium"]
            }`}
          >
            {task.priority}
          </span>
          {task.category && (
            <span className="text-[9px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 font-semibold">
              {task.category.replace(/_/g, " ")}
            </span>
          )}
          {task.due_date && (
            <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" /> {fmtDate(task.due_date)}
            </span>
          )}
          {task.recurrence && task.recurrence !== "none" && (
            <span className="text-[9px] uppercase tracking-wider rounded-full bg-blue-50 text-blue-700 px-1.5 py-0.5 font-semibold">
              {task.recurrence}
            </span>
          )}
        </div>
      </div>
      {(onEdit || onDelete) && !compact && (
        <div className="flex items-center gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(task)}
              data-testid={`task-edit-${task.id}`}
              className="text-[10px] text-slate-500 hover:text-slate-900 px-1.5 py-0.5"
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(task)}
              data-testid={`task-delete-${task.id}`}
              className="text-rose-500 hover:bg-rose-50 rounded p-1"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Tasks
// ===========================================================================
export default function TasksView() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/founder/tasks");
      setTasks(r.data?.tasks || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const grouped = useMemo(() => {
    const buckets = { todo: [], in_progress: [], done: [] };
    for (const t of tasks) {
      const k = t.status || "todo";
      if (buckets[k]) buckets[k].push(t);
    }
    return buckets;
  }, [tasks]);

  const moveTo = async (task, newStatus) => {
    try {
      await api.patch(`/founder/tasks/${task.id}`, { status: newStatus });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleComplete = async (task) => {
    await moveTo(task, task.status === "done" ? "todo" : "done");
  };

  const remove = async (task) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/founder/tasks/${task.id}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDrop = async (task, newStatus) => {
    if (!task || task.status === newStatus) return;
    await moveTo(task, newStatus);
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="founder-os-tasks">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {tasks.length} tasks · drag between columns to change status.
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="task-create-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> New task
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {TASK_STATUSES.map((s) => (
          <KanbanColumn
            key={s.id}
            status={s}
            tasks={grouped[s.id] || []}
            onDrop={handleDrop}
            onComplete={toggleComplete}
            onEdit={setEditing}
            onDelete={remove}
          />
        ))}
      </div>

      {creating && (
        <TaskDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <TaskDialog
          task={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function KanbanColumn({ status, tasks, onDrop, onComplete, onEdit, onDelete }) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const raw = e.dataTransfer.getData("text/plain");
        if (!raw) return;
        try {
          const task = JSON.parse(raw);
          onDrop(task, status.id);
        } catch {
          /* ignore malformed payload */
        }
      }}
      className={`rounded-xl border bg-white p-3 min-h-[300px] transition-colors ${
        over ? "border-slate-900 bg-slate-50" : "border-slate-200"
      }`}
      data-testid={`tasks-column-${status.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">
          {status.label}
        </div>
        <span className="text-[10px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 font-semibold">
          {tasks.length}
        </span>
      </div>
      <div className="space-y-1">
        {tasks.map((t) => (
          <div
            key={t.id}
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/plain", JSON.stringify(t))}
            className="cursor-grab active:cursor-grabbing"
          >
            <TaskRow
              task={t}
              onComplete={onComplete}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </div>
        ))}
        {tasks.length === 0 && (
          <div className="text-xs text-slate-400 text-center py-6">Drop tasks here.</div>
        )}
      </div>
    </div>
  );
}

function TaskDialog({ task, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task?.title || "",
    priority: task?.priority || "medium",
    category: task?.category || "clinic_outreach",
    status: task?.status || "todo",
    due_date: task?.due_date ? task.due_date.slice(0, 10) : "",
    notes: task?.notes || "",
    recurrence: task?.recurrence || "none",
    is_top_priority: !!task?.is_top_priority,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        due_date: form.due_date || null,
        notes: form.notes || null,
      };
      if (task) {
        await api.patch(`/founder/tasks/${task.id}`, payload);
        toast.success("Task updated.");
      } else {
        await api.post("/founder/tasks", payload);
        toast.success("Task created.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={task ? "Edit task" : "New task"}
      eyebrow="Founder OS"
      onClose={onClose}
      testId="task-dialog"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold rounded-full px-3 py-1.5 text-slate-600 hover:bg-slate-100"
            data-testid="task-dialog-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !form.title.trim()}
            data-testid="task-dialog-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <Field label="Title">
        <input
          type="text"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          autoFocus
          data-testid="task-dialog-title"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Priority">
          <select
            value={form.priority}
            onChange={(e) => set("priority", e.target.value)}
            data-testid="task-dialog-priority"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Field>
        <Field label="Category">
          <select
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            data-testid="task-dialog-category"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {TASK_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid="task-dialog-status"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            {TASK_STATUSES.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Recurrence">
          <select
            value={form.recurrence}
            onChange={(e) => set("recurrence", e.target.value)}
            data-testid="task-dialog-recurrence"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="none">None</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </Field>
      </div>
      <Field label="Due date">
        <input
          type="date"
          value={form.due_date}
          onChange={(e) => set("due_date", e.target.value)}
          data-testid="task-dialog-due"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
      <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
        <input
          type="checkbox"
          checked={form.is_top_priority}
          onChange={(e) => set("is_top_priority", e.target.checked)}
          data-testid="task-dialog-top-priority"
        />
        Pin to today's top priorities
      </label>
      <Field label="Notes">
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="task-dialog-notes"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </Field>
    </ModalShell>
  );
}

// ===========================================================================
// Clinic + Vendor pipelines (shared kanban view)
